import { motion } from "framer-motion";
import { Ship, Plane, Calculator, Link2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import "./ServicesSection.css";

export function ServicesSection() {
  const { t, language } = useLanguage();

  const services = [
    {
      icon: Ship,
      title: t("services.shipping.title"),
      description: t("services.shipping.desc"),
      features: [t("services.shipping.f1"), t("services.shipping.f2"), t("services.shipping.f3")],
    },
    {
      icon: Plane,
      title: t("services.air.title"),
      description: t("services.air.desc"),
      features: [t("services.air.f1"), t("services.air.f2"), t("services.air.f3")],
    },
    {
      icon: Calculator,
      title: t("services.pricing.title"),
      description: t("services.pricing.desc"),
      features: [t("services.pricing.f1"), t("services.pricing.f2"), t("services.pricing.f3")],
    },
    {
      icon: Link2,
      title: t("services.settlement.title"),
      description: t("services.settlement.desc"),
      features: [t("services.settlement.f1"), t("services.settlement.f2"), t("services.settlement.f3")],
    },
  ];

  return (
    <section id="shipping" className="py-24 bg-muted/30 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-secondary/20 via-accent/15 to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/4 w-[300px] h-[300px] bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-4">
            {t("services.badge")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("services.title")}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("services.desc")}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative service-card-wrapper"
            >

              {/* Rainbow border wrapper - must be outside for ::before to work */}
              <div className="service-border-wrapper relative h-full rounded-2xl transition-all duration-300">
                <div className="relative h-full p-6 rounded-2xl bg-card backdrop-blur-xl border border-border group-hover:border-transparent transition-all duration-500 overflow-hidden">
                  {/* Gradient background on hover - same as Get Started button */}
                  <div className="absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-ai" />

                  {/* Wave pattern overlay */}
                  <div className="absolute inset-0 z-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500 wave-pattern" />

                  {/* Content */}
                  <div className="relative z-20 flex flex-col h-full">
                    <div className="w-14 h-14 bg-white/60 dark:bg-white/10 backdrop-blur-xs rounded-xl flex items-center justify-center mb-5 border border-white/40 group-hover:bg-white group-hover:border-white group-hover:shadow-lg transition-all duration-300">
                      <service.icon className="w-7 h-7 text-secondary transition-colors" />
                    </div>

                    <h3 className="text-xl font-semibold text-foreground group-hover:text-primary-foreground mb-3 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground group-hover:text-primary-foreground/80 mb-4 text-sm leading-relaxed transition-colors">
                      {service.description}
                    </p>

                    <ul className="space-y-2 mb-5 flex-grow">
                      {service.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-primary-foreground/70 transition-colors"
                        >
                          <div className="w-1.5 h-1.5 bg-secondary group-hover:bg-primary-foreground rounded-full transition-colors" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="group/btn w-fit text-secondary hover:text-white hover:bg-transparent group-hover:text-white transition-all p-0"
                    >
                      {t("services.learnMore")}
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 flex justify-center"
        >
          <Link
            to={
              language === "zh"
                ? "/zh/compare/cargonova-vs-cargowise"
                : language === "th"
                ? "/th/compare/cargonova-vs-cargowise"
                : "/compare/cargonova-vs-cargowise"
            }
            className="inline-flex items-center gap-1.5 text-secondary font-medium hover:underline"
          >
            {t("services.compareLink")}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
